import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import {
  buttonLabels,
  getDefaultQuoteValues,
  quoteDocumentFieldNames,
  quoteHeader,
  quoteNotificationMessages,
  quoteSignatureTerms,
  quoteSignatureTermsDetails,
} from "./constants";
import { Button, message } from "antd";
import styles from "./QuoteEdit.module.scss";
import tnlogo from "../../../assets/images/TNLogo.svg";
import InputField from "../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import Form from "../../Form";
import DocumentServices from "../../../services/documents.services";
import { useLocation, useHistory } from "react-router-dom";
import TableComponent from "../TableComponent";
import { individualContent } from "../../../Types/documentsTypes";
import { appRoutes } from "../../../constants/appRoutes";
import TNLoader from "../../../reusable-components/TNLoader";
import { hasAnyEmptyField } from "../../../utils/emptyStringCheck";

const QuoteEdit = () => {
  const location = useLocation();
  const navigate = useHistory();
  const id = location?.pathname?.split("/")?.pop();
  const { FetchDocumentDetails, SaveDocumentAttributes } = DocumentServices();
  const { data } = FetchDocumentDetails({});
  const { mutateAsync: saveDocumentAttributes, isPending: isSavePending } =
    SaveDocumentAttributes();
  const defaultValues = getDefaultQuoteValues(data, id);
  const handleCancel = () => {
    navigate.push(appRoutes.LIST_DOCUMENTS);
  };
  return (
    <>
      <TNLoader isLoading={isSavePending} />
      <Form defaultValues={defaultValues}>
        {({ getValues }) => {
          const buildPayload = () => {
            const payload: individualContent = {
              id: id,
              companyName: getValues(quoteDocumentFieldNames.companyName),
              companyAddress: getValues(quoteDocumentFieldNames.companyAddress),
              companyContact: getValues(quoteDocumentFieldNames.companyContact),
              quoteAddressTerms: getValues(
                quoteDocumentFieldNames.quoteAddressTerms
              ),
              mainDescription: getValues(
                quoteDocumentFieldNames.mainDescription
              ),
              paymentTerms: getValues(quoteDocumentFieldNames.paymentTerms),
              signatureTerms: getValues(quoteDocumentFieldNames.signatureTerms),
              quoteHeader: getValues(quoteDocumentFieldNames.quoteHeader),
            };
            return payload;
          };
          const handleSave = async () => {
            const payload = buildPayload();
            if (hasAnyEmptyField(payload)) {
              message.error(quoteNotificationMessages.emptyFieldMessage);
              return;
            }
            await saveDocumentAttributes(payload, {
              onSuccess: () => {
                message.success(quoteNotificationMessages.saveSuccessMessage);
                navigate.push(appRoutes.LIST_DOCUMENTS);
              },
              onError: () => {
                message.error(quoteNotificationMessages.saveFailureMessage);
              },
            });
          };

          return (
            <div className={styles.quoteEditTemplateContainer}>
              <div className={styles.buttonContainer}>
                <div className={styles.cancelButtonContainer}>
                  <Button
                    size={ButtonSize.LARGE}
                    shape={ButtonShape.DEFAULT}
                    onClick={handleCancel}
                  >
                    {buttonLabels.cancel}
                  </Button>
                </div>
                <div className={styles.saveButtonContainer}>
                  <Button
                    type={ButtonType.PRIMARY}
                    size={ButtonSize.LARGE}
                    shape={ButtonShape.DEFAULT}
                    onClick={handleSave}
                  >
                    {buttonLabels.save}
                  </Button>
                </div>
              </div>
              <div className={styles.quoteContentContainer}>
                <div className={styles.quoteLogoAndAddressContainer}>
                  <div className={styles.quoteLogoContainer}>
                    <img src={tnlogo} />
                  </div>
                  <div className={styles.quoteAddressContainer}>
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={45}
                      type={InputfieldTypes.TEXT_AREA}
                      name={quoteDocumentFieldNames.companyName}
                      className={styles.addressInputFields}
                    />
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={90}
                      type={InputfieldTypes.TEXT_AREA}
                      name={quoteDocumentFieldNames.companyAddress}
                      className={styles.addressInputFields}
                    />
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={45}
                      type={InputfieldTypes.TEXT_AREA}
                      name={quoteDocumentFieldNames.companyContact}
                      className={styles.addressInputFields}
                    />
                  </div>
                </div>
                <div className={styles.quoteHeaderContainer}>
                  <div className={styles.quoteHeader}>
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={30}
                      type={InputfieldTypes.TEXT_AREA}
                      name={quoteDocumentFieldNames.quoteHeader}
                    />
                  </div>
                  <div className={styles.quoteAddressTerms}>
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={125}
                      type={InputfieldTypes.TEXT_AREA}
                      name={quoteDocumentFieldNames.quoteAddressTerms}
                    />
                  </div>
                </div>
                <div className={styles.table}>
                  <TableComponent />
                </div>

                <div className={styles.quoteDescriptionContainer}>
                  <InputField
                    textAreaRows={10}
                    showCount
                    maxLength={1000}
                    type={InputfieldTypes.TEXT_AREA}
                    name={quoteDocumentFieldNames.mainDescription}
                  />
                </div>
                <div className={styles.quotePaymentTermsContainer}>
                  <div className={styles.contentHeader}>
                    {quoteHeader.paymentHeader}
                  </div>
                  <InputField
                    textAreaRows={5}
                    showCount
                    maxLength={500}
                    type={InputfieldTypes.TEXT_AREA}
                    name={quoteDocumentFieldNames.paymentTerms}
                  />
                </div>
                <div className={styles.quoteSignatureContainer}>
                  <div className={styles.contentHeader}>
                    {quoteHeader.signature}
                  </div>
                  <InputField
                    textAreaRows={5}
                    showCount
                    maxLength={500}
                    type={InputfieldTypes.TEXT_AREA}
                    name={quoteDocumentFieldNames.signatureTerms}
                  />
                </div>
                <div className={styles.quoteSectionBottomContainer}>
                  <div className={styles.quoteSectionFirstContainer}>
                    <div className={styles.quoteSignContainer}>
                      {quoteSignatureTerms.leftHeader}
                    </div>
                    {Object?.values(quoteSignatureTermsDetails)?.map((val) => {
                      return (
                        <div className={styles.quoteDetailsContainer}>
                          {val}
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.quoteSectionSecondContainer}>
                    {quoteSignatureTerms.date}
                  </div>
                  <div className={styles.quoteSectionThirdContainer}>
                    <div className={styles.quoteSignContainer}>
                      {quoteSignatureTerms.rightHeader}
                    </div>
                    {Object?.values(quoteSignatureTermsDetails)?.map((val) => {
                      return (
                        <div className={styles.quoteDetailsContainer}>
                          {val}
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.quoteSectionFourthContainer}>
                    {quoteSignatureTerms.date}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Form>
    </>
  );
};
export default QuoteEdit;
